import { useQueryClient } from '@tanstack/vue-query';
import { defineStore } from 'pinia';
import type { ConfirmationOptions } from 'primevue/confirmationoptions';
import { useConfirm } from 'primevue/useconfirm';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { mixPanelEvents } from '@/common/constants';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { track } from '@/helpers/analytics/mixpanel';
import { axios, sanctumCookie } from '@/helpers/axios';
import {
  leaveAuthBreadcrumb,
  notifyAuthError,
} from '@/helpers/bugsnag';

type CustomConfirmationOptions = ConfirmationOptions & { closeable?: boolean };

export const useAuthStore = defineStore('auth', () => {
  const queryClient = useQueryClient();
  // TODO: on full SPA this will have to change
  const user = ref(window.__DATA__?.user);
  const memberData = ref(null);
  const shops = ref([]);
  const invoices = ref([]);
  const invoice = ref(null);
  const invoiceLoading = ref(null);
  const loadingMemberData = ref(true);
  const loadingShops = ref(true);
  const loadingChangePayment = ref(false);
  const loadingCancelSubscription = ref(false);
  const loadingEmailChange = ref(false);
  const loadingPasswordChange = ref(false);
  const selectedReasons = ref<string[]>([]);
  const comments = ref<string>('');
  const newEmail = ref<string>('');
  const newsletter = ref(false);
  const isTokenRevoked = ref(false);
  const hasTokenRevoked = ref(false);
  const isTokenCheckerLoading = ref(true);
  const loadingShopItem = ref([]);

  const toast = useTrackedToast();
  const confirm = useConfirm();
  const route = useRoute();
  const router = useRouter();

  const isShowTokenRevokedBanner = computed(() => isTokenRevoked.value && user.value?.current_shop?.shop_id);

  const logout = async () => {
    // Clear session storage that might contain cached user data
    sessionStorage.clear();
    // Navigate to logout
    window.location.href = '/logout';
  };

  const getInvoices = async () => {
    try {
      const response = await axios.get('/settings/invoices/' + user.value?.member_id);
      invoices.value = response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const getInvoice = async (payload) => {
    invoiceLoading.value = payload.txn_id;
    try {
      const response = await axios.get('/settings/invoice', {
        params: {
          txn_id: payload.txn_id,
          member_id: user.value?.member_id,
        },
      });
      invoice.value = response.data;

      track('Viewed Invoice', {
        'Page Title': 'Settings',
        'Erank Version': 3,
      });
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      invoiceLoading.value = null;
    }
  };

  const getMemberDetails = async () => {
    loadingMemberData.value = true;
    try {
      const response = await axios.get(`/settings/member/${user.value?.member_id}`);
      memberData.value = response.data;
      memberData.value.billing_info = {
        ...memberData.value.billing_info,
        email: memberData.value.email,
        member_id: user.value?.member_id,
      };
    } catch (error) {
      console.error('Error fetching member details:', error);
    } finally {
      loadingMemberData.value = false;
    }
  };

  const updateMemberDetails = async (payload) => {
    try {
      await axios.post('/settings/member-info/update', payload);
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Billing information updated successfully.',
        life: 3000,
      });

      track('Edited Billing Information', {
        'Page Title': 'Settings',
        'Erank Version': 3,
      });
    } catch (error) {
      console.error('Error updating member details:', error);
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} MEMBER_DETAILS_UPDATE_FAILED` : 'MEMBER_DETAILS_UPDATE_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update billing information. Please try again later.',
        life: 3000,
        errorCode,
      });
    }
  };

  const getMemberShops = async () => {
    loadingShops.value = true;
    try {
      const response = await axios.get(`/settings/shop-list/${user.value?.member_id}`);
      shops.value = response.data.shops;
    } catch (error) {
      console.error('Error fetching member shops:', error);
    } finally {
      loadingShops.value = false;
    }
  };

  const removeShop = async (shopId) => {
    try {
      await axios.post('/account/remove-shop', { shop_id: shopId });
      shops.value = shops.value.filter(shop => shop.shop_id !== shopId);
      queryClient.invalidateQueries({ queryKey: ['member-shops'] });
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Shop removed successfully.',
        life: 3000,
      });
      track('Removed Shop', {
        'Page Title': 'Settings',
        'Erank Version': 3,
      });
    } catch (error) {
      console.error('Error removing shop:', error);
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} SHOP_REMOVE_FAILED` : 'SHOP_REMOVE_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to remove shop. Please try again later.',
        life: 3000,
        errorCode,
      });
    }
  };

  const handleChangePaymentMethod = async () => {
    loadingChangePayment.value = true;
    try {
      const response = await axios.get('/subscription/payment-method-link');
      window.location.href = response.data.payment_method_link;
    } catch (error) {
      loadingChangePayment.value = false;
      track(mixPanelEvents.ERROR_CHANGING_PAYMENT_METHOD, {
        'Source': 'Subscription Panel',
        'Page Title': 'Settings',
        'Error Message': error.response?.data || error.message,
        'Erank Version': 3,
      });
      console.error('Error changing payment method:', error);
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} PAYMENT_METHOD_CHANGE_FAILED` : 'PAYMENT_METHOD_CHANGE_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to change payment method. Please try again later.',
        life: 3000,
        errorCode,
      });
    }
  };

  const cancelSubscription = async (cancelImmediately = false, isAnnual = false) => {
    const answers = [...selectedReasons.value, comments.value];

    track('Canceled Subscription', {
      'Reason': answers.toString(),
      'Comment': comments.value,
      'CancelType': cancelImmediately ? 'Immediate' : 'End of Term',
    });
    loadingCancelSubscription.value = true;
    const url = '/account/cancel-subscription-settings';
    if (comments.value) {
      selectedReasons.value.push(comments.value);
    }
    const response = await axios.post(url, {
      cancel_type: cancelImmediately ? 'now' : 'eot',
      reasons: selectedReasons.value.join(','),
    });
    if (response.data.status === true) {
      track(mixPanelEvents.CANCELLED_SUBSCRIPTION, {
        'Source': 'Subscription Panel',
        'Page Title': 'Settings',
        'Erank Version': 3,
      });
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response.data.message,
        life: 10000,
      });

      if(!isAnnual && cancelImmediately) {
        window.location.reload();
      } else {
        await getMemberDetails();
      }
    } else {
      track(mixPanelEvents.ERROR_CANCEL_SUBSCRIPTION, {
        'Source': 'Subscription Panel',
        'Page Title': 'Settings',
        'Erank Version': 3,
      });

      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: response.data.msg,
        life: 3000,
      });
    }
    loadingCancelSubscription.value = false;
  };

  const handlePaddleCancelSubscription= async () => {
    loadingCancelSubscription.value = true;
    track(mixPanelEvents.CONFIRM_CANCEL_SUBSCRIPTION, {
      'Source': 'Subscription Panel',
      'Page Title': 'Settings',
      'Erank Version': 3,
    });
    try {
      const response = await axios.get('/account/is-recent-subscription');
      if (response.data.is_annual) {
        if (response.data.is_recent) {
          track(mixPanelEvents.OPEN_CANCEL_SUBSCRIPTION_CONFIRMATION_DIALOG, {
            'Source': 'Subscription Panel',
            'Page Title': 'Settings',
            'Erank Version': 3,
          });
          confirm.require({
            group: 'headless',
            message: 'Since you recently started your annual subscription, you can either cancel immediately and request a refund, or cancel at the end of your subscription\'s term and use your subscription for the year.',
            header: 'Cancel Subscription',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Cancel Now',
            rejectLabel: 'Cancel at End of Term',
            hasAcceptIcon: false,
            accept: async () => {
              await cancelSubscription(true, true);
            },
            reject: async () => {
              await cancelSubscription();
            },
          } as CustomConfirmationOptions);
        } else {
          await cancelSubscription();
        }
      } else {
        await cancelSubscription();
      }
    } catch (error) {
      loadingCancelSubscription.value = false;
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} SUBSCRIPTION_CANCEL_FAILED` : 'SUBSCRIPTION_CANCEL_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to change payment method. Please try again later.',
        life: 3000,
        errorCode,
      });
    } finally {
      loadingCancelSubscription.value = false;
    }
  };

  const handleCancelSubscription = async () => {
    loadingCancelSubscription.value = true;
    try {
      if (comments.value) {
        selectedReasons.value.push(comments.value);
      }
      await axios.post('/account/manual-cancel', {
        reasons: selectedReasons.value.join(','),
      });
      track(mixPanelEvents.CANCELLED_SUBSCRIPTION, {
        'Source': 'Subscription Panel',
        'Page Title': 'Settings',
        'Erank Version': 3,
      });
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Subscription canceled successfully.',
        life: 3000,
      });

      window.location.reload();
    } catch (error) {
      loadingCancelSubscription.value = false;
      track(mixPanelEvents.ERROR_CANCEL_SUBSCRIPTION, {
        'Source': 'Subscription Panel',
        'Page Title': 'Settings',
        'Erank Version': 3,
      });
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to cancel subscription. Please try again later.',
        life: 3000,
      });
    }
  };

  const changeEmail = async (payload) => {
    loadingEmailChange.value = true;
    try {
      await axios.post('/account/change-login-email', payload);
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Email changed successfully. Please check your inbox for a confirmation link.',
        life: 3000,
      });
    } catch (error) {
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} EMAIL_CHANGE_FAILED` : 'EMAIL_CHANGE_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data || 'Failed to change email. Please try again later or try a different email address.',
        life: 3000,
        errorCode,
      });
    } finally {
      loadingEmailChange.value = false;
    }
  };

  const changeToMagiclink = async (payload) => {
    loadingEmailChange.value = true;
    try {
      await axios.post('/account/change-email', payload);

      if (!payload.is_account_panel) {
        user.value.google_email = null;
      }

      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Email changed successfully.',
        life: 3000,
      });
    } catch (error) {
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} EMAIL_UNLINK_FAILED` : 'EMAIL_UNLINK_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data || 'Failed to change email. Please try again later or try a different email address.',
        life: 3000,
        errorCode,
      });
    } finally {
      loadingEmailChange.value = false;
    }
  };

  const changePassword = async (payload) => {
    loadingPasswordChange.value = true;
    try {
      await axios.post('/account/change-password', payload);
      track(mixPanelEvents.CHANGED_PASSWORD, {
        'Source': 'Password Change Dialog',
        'Page Title': 'Settings',
        'Erank Version': 3,
      });
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Password changed successfully.',
        life: 3000,
      });
      window.location.reload();
    } catch (error) {
      track(mixPanelEvents.ERROR_CHANGING_PASSWORD, {
        'Source': 'Password Change Dialog',
        'Page Title': 'Settings',
        'Error Message': error.response?.data || error.message,
        'Erank Version': 3,
      });
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} PASSWORD_CHANGE_FAILED` : 'PASSWORD_CHANGE_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data || 'Failed to change password. Please try again later.',
        life: 3000,
        errorCode,
      });
    } finally {
      loadingPasswordChange.value = false;
    }
  };

  const deleteAccount = async () => {
    loadingEmailChange.value = true;
    try {
      await axios.post('/account/remove-account');
      track('Deleted Account', {
        'Source': 'Danger Zone Panel',
        'Page Title': 'Settings',
        'Erank Version': 3,
      });
      await logout();
      window.location.reload();
    } catch (error) {
      track(mixPanelEvents.ERROR_DELETING_ACCOUNT, {
        'Source': 'Danger Zone Panel',
        'Page Title': 'Settings',
        'Error Message': error.response?.data || error.message,
        'Erank Version': 3,
      });
      const statusCode = error.response?.status ? `${error.response.status}` : '';
      const errorCode = statusCode ? `${statusCode} ACCOUNT_DELETE_FAILED` : 'ACCOUNT_DELETE_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete account. Please try again later.',
        life: 3000,
        errorCode,
      });
    } finally {
      loadingEmailChange.value = false;
    }
  };

  const handleCredentialResponse = async (response: { credential: string }) => {
    localStorage.setItem('google', JSON.stringify(response));
    const isLogin = route.name === 'login.entry';
    const breadcrumbMessage = isLogin ? 'Google One Tap login success' : 'Google One Tap credential received (registration)';
    const breadcrumbMessage1 = isLogin ? 'Google One Tap login success' : 'Google One Tap registration success';

    leaveAuthBreadcrumb(breadcrumbMessage, {
      hasCredential: Boolean(response?.credential),
    });
    // Send the credential to your backend for verification
    await sanctumCookie();
    await axios.post('/auth/google/onetap', { credential: response.credential, ...route.query, newsletter: newsletter.value })
      .then(res => {
        leaveAuthBreadcrumb(breadcrumbMessage1, {
        });
        if (res.data) {
          if (res.data.redirect.includes('oauth_signup_attempt')) {
            track(mixPanelEvents.STARTED_ONBOARDING, {
              'Newsletter opt-in': false,
              'Used Magic Link': false,
              'Used Google SSO': true,
              'Clicked Sign in': false,
              'Erank Version': 3,
            });
          } else {
            track(mixPanelEvents.LOGGED_IN, {
              'Login Method': 'Google SSO',
              'Erank Version': 3,
              'First Time': 'True',
            });
          }
          window.location.href = res.data.redirect;
        }
      })
      .catch(err => {
        notifyAuthError(err, {
          stage: 'google-one-tap',
          status: err.response?.status,
        });
        // Handle error, show toast or message
        if (err.response?.data?.redirect) {
          router.push(err.response?.data?.redirect);
        } else {
          toast.add({ severity: 'error', summary: 'Google Login Failed', detail: err.response?.data?.message || err.message, life: 5000 });
        }
      });
  };

  const retryGoogleOneTap = async () => {
    const googleCredential = JSON.parse(localStorage.getItem('google') || '{}');
    await sanctumCookie();
    await axios.post('/auth/google/onetap', { credential: googleCredential?.credential, ...route.query, newsletter: newsletter.value }).then(() => {
      window.location.href = '/survey/registration?google=true&oauth_signup_attempt=1';
    }).catch(err => {
      if (err.response?.data?.redirect) {
        router.push(err.response?.data?.redirect);
      } else {
        toast.add({ severity: 'error', summary: 'Google Login Failed', detail: err.response?.data?.message || err.message, life: 5000 });
      }
    });
  };

  const getUser = async () => {
    const response = await axios.get('/user');
    user.value = response.data;
  };

  const checkAuthStatus = async () => {
    const response = await axios.get('/auth/status');
    return response.data;
  };

  const checkTokenValidity = async () => {
    isTokenCheckerLoading.value = true;
    await axios.get('/oauth/check-token-validity', {
      params: {
        shop_id: user.value?.current_shop_id,
        member_id: user.value?.member_id,
      },
    }).then(res => {
      isTokenRevoked.value = res.data.revoked;
    }).catch(err => {
      console.error('Error checking token validity:', err);
      isTokenRevoked.value = false;
      isTokenCheckerLoading.value = false;
    }).finally(() => {
      isTokenCheckerLoading.value = false;
    });
  };

  const checkShopTokenValidity = async (shopId: string) => {
    loadingShopItem.value.push(shopId);
    await axios.get('/oauth/check-token-validity', {
      params: {
        shop_id: shopId,
        member_id: user.value?.member_id,
      },
    }).then(res => {
      hasTokenRevoked.value = res.data.revoked;
      shops.value = shops.value.map(shop => {
        if (shop.shop_id === shopId) {
          shop.is_revoked = res.data.revoked;
        }
        return shop;
      });
    }).finally(() => {
      loadingShopItem.value = loadingShopItem.value.filter(id => id !== shopId);
    });
  };

  const currentPlan = computed(() => user.value ? window.__PLANS__.ids[user.value?.plan_id as number] : 'free');

  const isUserLogged = computed<boolean>(() => !!user.value);

  const hasShopConnected = computed<boolean>(() => window.__DATA__?.user?.connected_shops > 0) ;
  const hasOneShopConnected = computed<boolean>(() => window.__DATA__?.user?.connected_shops === 1);

  // Redirect Users to the referral page if they are on a trial plan or have a referrer and not yet on a trial plan
  const redirectToReferralPage = computed<boolean>(() => {
    const trialEndDate = window.__DATA__?.user?.trial_end_date;
    const withReferrer = window.__DATA__?.user?.referrer != null && window.__DATA__?.user?.referrer != '';

    if (!trialEndDate && !withReferrer) {
      return false;
    } else if (!trialEndDate && withReferrer) {
      return true;
    }

    const trialEndDateObj = new Date(trialEndDate);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    return trialEndDateObj >= currentDate;
  });

  const currentlyOnTrialOrEndOfTrial = computed<boolean>(() => {
    const trialEndDate = window.__DATA__?.user?.trial_end_date;

    if (!trialEndDate) {
      return false;
    }
    const trialEndDateObj = new Date(trialEndDate);

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    return trialEndDateObj >= currentDate || trialEndDateObj < currentDate;
  });

  const currentShopId = computed(() => user.value?.current_shop_id);

  return {
    currentPlan,
    user,
    memberData,
    shops,
    invoices,
    invoice,
    invoiceLoading,
    loadingMemberData,
    loadingShops,
    loadingChangePayment,
    loadingCancelSubscription,
    loadingEmailChange,
    loadingPasswordChange,
    selectedReasons,
    comments,
    newsletter,
    newEmail,
    hasShopConnected,
    isUserLogged,
    redirectToReferralPage,
    currentlyOnTrialOrEndOfTrial,
    currentShopId,
    isTokenRevoked,
    isTokenCheckerLoading,
    isShowTokenRevokedBanner,
    hasTokenRevoked,
    hasOneShopConnected,
    getInvoices,
    getInvoice,
    getMemberDetails,
    updateMemberDetails,
    getMemberShops,
    removeShop,
    handleChangePaymentMethod,
    handleCancelSubscription,
    handlePaddleCancelSubscription,
    changeEmail,
    deleteAccount,
    handleCredentialResponse,
    retryGoogleOneTap,
    changePassword,
    changeToMagiclink,
    getUser,
    checkAuthStatus,
    checkTokenValidity,
    checkShopTokenValidity,
    loadingShopItem,
    logout,
  };
});
